import { api } from "../api";
import { iconoLapiz, iconoBuscar, iconoMas, iconoGrupos } from "../iconos";
import type { Producto, Categoria, AreaProduccion, GrupoProduccion } from "../../lib/types";

type ProductoFila = Producto & { categoria: string; area: string; grupo: string | null };

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}

const PAGINA = 15;

export async function renderProductos(contenedor: HTMLElement): Promise<void> {
  let categorias: Categoria[] = [];
  let areas: AreaProduccion[] = [];
  let grupos: GrupoProduccion[] = [];
  let todos: ProductoFila[] = [];
  let filtro = "";
  let pagina = 1;

  try {
    const [catList, meta, grupoList] = await Promise.all([
      api.get<Categoria[]>("/api/categorias"),
      api.get<{ areas: AreaProduccion[] }>("/api/meta"),
      api.get<GrupoProduccion[]>("/api/grupos"),
    ]);
    categorias = catList;
    areas = meta.areas;
    grupos = grupoList;
  } catch {
    /* ignore */
  }

  // Siguiente codigo libre para la familia (prefijo) de una categoria.
  function siguienteCodigo(catId: number): string {
    const pref = categorias.find((c) => c.id === catId)?.prefijo;
    if (!pref) return "";
    const desde = pref * 1000;
    const hasta = desde + 999;
    const enBanda = todos.map((p) => p.codigo).filter((c) => c >= desde && c <= hasta);
    return String(enBanda.length ? Math.max(...enBanda) + 1 : desde + 1);
  }

  function avisoModal(mensaje: string): void {
    dlg.innerHTML = `
      <div class="tarjeta w-[24rem] max-w-full border-0 shadow-none">
        <h2 class="mb-2 text-lg font-bold text-red-700">Codigo duplicado</h2>
        <p class="text-sm text-masa-700">${mensaje}</p>
        <div class="mt-4 flex justify-end">
          <button id="ok" class="btn-primario">Entendido</button>
        </div>
      </div>`;
    dlg.querySelector("#ok")?.addEventListener("click", () => dlg.close());
    dlg.showModal();
  }
  const opc = (arr: Array<{ id: number; nombre: string }>, sel?: number) =>
    arr.map((x) => `<option value="${x.id}"${x.id === sel ? " selected" : ""}>${esc(x.nombre)}</option>`).join("");

  // El grupo es opcional: sin grupo el articulo sale solo en la hoja de produccion.
  const opcGrupo = (sel?: number | null) =>
    `<option value=""${sel ? "" : " selected"}>— Sin grupo —</option>` +
    grupos
      .filter((g) => g.activo || g.id === sel)
      .map((g) => `<option value="${g.id}"${g.id === sel ? " selected" : ""}>${esc(g.nombre)}</option>`)
      .join("");

  contenedor.innerHTML = `
    <div class="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 class="titulo-pagina">Productos</h1>
        <p class="subtitulo">Gestiona el catalogo de panaderia y reposteria.</p>
      </div>
      <button id="verGrupos" class="btn-destacado">${iconoGrupos} Grupos de produccion</button>
    </div>

    <details class="tarjeta mb-4">
      <summary class="flex cursor-pointer list-none items-center gap-2 text-sm font-bold uppercase tracking-wide text-masa-600">
        <span class="text-masa-500">${iconoMas}</span> Nuevo producto
      </summary>
      <form id="f" class="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-6">
        <label class="sm:col-span-2"><span class="etiqueta">Codigo</span>
          <input id="codigo" type="number" class="campo" placeholder="1001" /></label>
        <label class="sm:col-span-4"><span class="etiqueta">Descripcion</span>
          <input id="descripcion" class="campo" placeholder="Ej: Bizcochos Kg" /></label>
        <label class="sm:col-span-2"><span class="etiqueta">Categoria</span>
          <select id="categoria_id" class="campo">${opc(categorias)}</select></label>
        <label class="sm:col-span-2"><span class="etiqueta">Area</span>
          <select id="area_id" class="campo">${opc(areas)}</select></label>
        <label class="sm:col-span-2"><span class="etiqueta">Grupo de produccion</span>
          <select id="grupo_id" class="campo">${opcGrupo()}</select></label>
        <label><span class="etiqueta">Unidades por paquete</span>
          <input id="empaque" type="number" min="1" value="1" class="campo" /></label>
        <label><span class="etiqueta">IVA</span>
          <select id="iva_tasa" class="campo">
            <option value="0">0%</option><option value="10">10%</option><option value="22">22%</option>
          </select></label>
        <p class="text-xs text-masa-500 sm:col-span-6">
          <b>Unidades por paquete</b>: cuantas unidades salen de cada bulto o kilo. Es lo que
          multiplica la hoja de produccion (un pack x8 son 8 unidades; el kilo de flauta, 3).
          El <b>grupo</b> junta los distintos envases del mismo pan en un solo total para el panadero.
        </p>
        <div class="flex items-end sm:col-span-6">
          <button class="btn-primario">${iconoMas} Agregar</button>
          <span id="err" class="ml-3 self-center text-sm text-red-600"></span>
        </div>
      </form>
    </details>

    <div class="relative mb-3 max-w-sm">
      <span class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-masa-400">${iconoBuscar}</span>
      <input id="buscar" class="campo pl-10" placeholder="Buscar por codigo o descripcion..." />
    </div>

    <div class="tarjeta overflow-x-auto p-0">
      <table class="tabla">
        <thead><tr>
          <th>Codigo</th><th>Descripcion</th><th>Categoria</th><th>Area</th><th>Grupo</th>
          <th class="text-right">Un./paq.</th><th class="text-right">IVA</th><th class="text-right">Acciones</th>
        </tr></thead>
        <tbody id="tbody"></tbody>
      </table>
      <div id="pie" class="flex items-center justify-between border-t border-crema-200 px-3 py-2 text-sm text-masa-500"></div>
    </div>

    <dialog id="dlg"></dialog>
    <dialog id="dlgGrupos"></dialog>`;

  const tbody = contenedor.querySelector("#tbody") as HTMLElement;
  const pie = contenedor.querySelector("#pie") as HTMLElement;
  const err = contenedor.querySelector("#err") as HTMLElement;
  const dlg = contenedor.querySelector("#dlg") as HTMLDialogElement;
  const buscar = contenedor.querySelector("#buscar") as HTMLInputElement;
  const dlgGrupos = contenedor.querySelector("#dlgGrupos") as HTMLDialogElement;

  function filtrados(): ProductoFila[] {
    if (!filtro) return todos;
    const q = filtro.toLowerCase();
    return todos.filter(
      (p) => p.descripcion.toLowerCase().includes(q) || String(p.codigo).includes(q),
    );
  }

  function pintar(): void {
    const lista = filtrados();
    const totalPag = Math.max(1, Math.ceil(lista.length / PAGINA));
    if (pagina > totalPag) pagina = totalPag;
    const desde = (pagina - 1) * PAGINA;
    const pageItems = lista.slice(desde, desde + PAGINA);

    tbody.innerHTML =
      pageItems
        .map(
          (p) => `<tr>
            <td class="font-medium">${esc(p.codigo)}</td>
            <td>${esc(p.descripcion)}</td>
            <td>${esc(p.categoria)}</td>
            <td>${esc(p.area)}</td>
            <td class="text-masa-500">${p.grupo ? esc(p.grupo) : "—"}</td>
            <td class="text-right">x${esc(p.empaque)}</td>
            <td class="text-right">${esc(p.iva_tasa)}%</td>
            <td class="text-right"><button class="chip-accion" data-editar="${p.id}" title="Editar">${iconoLapiz}</button></td>
          </tr>`,
        )
        .join("") ||
      `<tr><td colspan="7" class="py-6 text-center text-masa-400">Sin resultados.</td></tr>`;

    const hasta = Math.min(desde + PAGINA, lista.length);
    pie.innerHTML = `
      <span>Mostrando ${lista.length ? desde + 1 : 0}–${hasta} de ${lista.length} productos</span>
      <span class="flex gap-1">
        <button id="prev" class="chip-accion" ${pagina <= 1 ? "disabled" : ""}>‹</button>
        <button id="next" class="chip-accion" ${pagina >= totalPag ? "disabled" : ""}>›</button>
      </span>`;
    pie.querySelector("#prev")?.addEventListener("click", () => { pagina--; pintar(); });
    pie.querySelector("#next")?.addEventListener("click", () => { pagina++; pintar(); });
  }

  async function cargar(): Promise<void> {
    tbody.innerHTML = `<tr><td colspan="7" class="py-6 text-center text-masa-400">Cargando...</td></tr>`;
    todos = await api.get<ProductoFila[]>("/api/productos?activo=1");
    pintar();
  }

  buscar.addEventListener("input", () => { filtro = buscar.value.trim(); pagina = 1; pintar(); });

  // Alta: sugerir codigo segun la categoria (prefijo)
  const selCat = contenedor.querySelector("#categoria_id") as HTMLSelectElement;
  const inpCod = contenedor.querySelector("#codigo") as HTMLInputElement;
  const sugerir = () => { inpCod.value = siguienteCodigo(Number(selCat.value)); };
  selCat.addEventListener("change", sugerir);

  const form = contenedor.querySelector("#f") as HTMLFormElement;
  form.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    err.textContent = "";
    const num = (id: string) => Number((contenedor.querySelector("#" + id) as HTMLInputElement).value);
    const txt = (id: string) => (contenedor.querySelector("#" + id) as HTMLInputElement).value.trim();
    if (!num("codigo") || !txt("descripcion")) {
      err.textContent = "Codigo y descripcion son obligatorios";
      return;
    }
    const dupProd = todos.find((p) => p.codigo === num("codigo"));
    if (dupProd) {
      avisoModal(`El codigo ${num("codigo")} ya existe en el sistema (producto: ${esc(dupProd.descripcion)}).`);
      return;
    }
    try {
      await api.post("/api/productos", {
        codigo: num("codigo"), descripcion: txt("descripcion"),
        categoria_id: num("categoria_id"), area_id: num("area_id"),
        empaque: num("empaque"), iva_tasa: num("iva_tasa"),
        grupo_id: num("grupo_id") || null,
      });
      form.reset();
      await cargar();
      sugerir();
    } catch (e) {
      err.textContent = e instanceof Error ? e.message : "Error";
    }
  });

  // Editar (delegacion)
  tbody.addEventListener("click", (ev) => {
    const btn = (ev.target as HTMLElement).closest("[data-editar]");
    if (!btn) return;
    const id = Number(btn.getAttribute("data-editar"));
    const p = todos.find((x) => x.id === id);
    if (p) abrirEdicion(p);
  });

  function abrirEdicion(p: ProductoFila): void {
    dlg.innerHTML = `
      <form method="dialog" class="tarjeta w-[28rem] max-w-full border-0 shadow-none">
        <h2 class="mb-1 text-lg font-bold">Editar producto</h2>
        <p class="mb-4 text-sm text-masa-500">Codigo #${esc(p.codigo)}</p>
        <div class="grid grid-cols-2 gap-3">
          <label class="col-span-2"><span class="etiqueta">Descripcion</span>
            <input id="e_desc" class="campo" value="${esc(p.descripcion)}" /></label>
          <label><span class="etiqueta">Categoria</span>
            <select id="e_cat" class="campo">${opc(categorias, p.categoria_id)}</select></label>
          <label><span class="etiqueta">Area</span>
            <select id="e_area" class="campo">${opc(areas, p.area_id)}</select></label>
          <label class="col-span-2"><span class="etiqueta">Grupo de produccion</span>
            <select id="e_grupo" class="campo">${opcGrupo(p.grupo_id)}</select></label>
          <label><span class="etiqueta">Unidades por paquete</span>
            <input id="e_emp" type="number" min="1" class="campo" value="${esc(p.empaque)}" /></label>
          <label><span class="etiqueta">IVA</span>
            <select id="e_iva" class="campo">
              ${[0, 10, 22].map((t) => `<option value="${t}"${t === p.iva_tasa ? " selected" : ""}>${t}%</option>`).join("")}
            </select></label>
          <label class="col-span-2 flex items-center gap-2 pt-1">
            <input id="e_activo" type="checkbox" ${p.activo ? "checked" : ""} class="h-4 w-4 accent-masa-600" />
            <span class="text-sm">Activo</span></label>
        </div>
        <p id="e_err" class="mt-2 text-sm text-red-600"></p>
        <div class="mt-4 flex justify-end gap-2">
          <button value="cancel" class="btn-sec">Cancelar</button>
          <button id="e_guardar" value="ok" class="btn-primario">Guardar</button>
        </div>
      </form>`;
    const eErr = dlg.querySelector("#e_err") as HTMLElement;
    dlg.querySelector("#e_guardar")?.addEventListener("click", async (ev) => {
      ev.preventDefault();
      eErr.textContent = "";
      const val = (id: string) => (dlg.querySelector("#" + id) as HTMLInputElement).value;
      try {
        await api.put(`/api/productos/${p.id}`, {
          descripcion: val("e_desc").trim(),
          categoria_id: Number(val("e_cat")),
          area_id: Number(val("e_area")),
          empaque: Number(val("e_emp")),
          iva_tasa: Number(val("e_iva")),
          activo: (dlg.querySelector("#e_activo") as HTMLInputElement).checked ? 1 : 0,
          grupo_id: Number(val("e_grupo")) || null,
        });
        dlg.close();
        await cargar();
      } catch (e) {
        eErr.textContent = e instanceof Error ? e.message : "Error";
      }
    });
    dlg.showModal();
  }

  // ------------------------------------------------------------ grupos
  // Un grupo junta los envases del mismo pan (Campana x1, x4, x8) para que la
  // hoja de produccion muestre un unico total en unidades. Se administra desde
  // aca; a que grupo va cada articulo se elige en el lapiz de cada fila.
  function pintarGrupos(): void {
    const filas = grupos
      .map((g) => {
        const miembros = todos.filter((p) => p.grupo_id === g.id);
        return `<tr class="border-t border-crema-100 align-top">
          <td class="py-2 pr-2">
            <input class="campo" data-nombre="${g.id}" value="${esc(g.nombre)}" />
            ${
              miembros.length
                ? `<details class="mt-1"><summary class="cursor-pointer text-xs text-masa-500">${miembros.length} articulo(s)</summary>
                     <ul class="mt-1 list-disc pl-5 text-xs text-masa-500">
                       ${miembros.map((m) => `<li>${esc(m.codigo)} ${esc(m.descripcion)} (x${esc(m.empaque)})</li>`).join("")}
                     </ul></details>`
                : `<span class="text-xs text-masa-400">Sin articulos todavia</span>`
            }
          </td>
          <td class="py-2 text-right whitespace-nowrap">
            <button class="btn-sec text-sm" data-guardar-grupo="${g.id}">Guardar</button>
            <button class="btn-sec ml-1 text-sm" data-borrar-grupo="${g.id}">Borrar</button>
          </td>
        </tr>`;
      })
      .join("");

    dlgGrupos.innerHTML = `
      <div class="tarjeta w-[34rem] max-w-full border-0 shadow-none">
        <h2 class="mb-1 text-lg font-bold">Grupos de produccion</h2>
        <p class="mb-4 text-sm text-masa-500">
          Los articulos de un mismo grupo salen sumados en una sola linea de la hoja de produccion
          (por ejemplo, toda la galleta de campana junta). Para poner un articulo en un grupo,
          editalo con el lapiz y elegi el grupo.
        </p>
        <div class="mb-3 flex gap-2">
          <input id="g_nuevo" class="campo" placeholder="Nombre del grupo (ej: Galleta de Campana)" />
          <button id="g_crear" class="btn-primario whitespace-nowrap">${iconoMas} Crear</button>
        </div>
        <p id="g_err" class="mb-2 text-sm text-red-600"></p>
        <div class="max-h-[22rem] overflow-y-auto">
          <table class="w-full text-sm">
            <tbody id="g_tbody">${
              filas || `<tr><td class="py-4 text-center text-masa-400">Todavia no hay grupos.</td></tr>`
            }</tbody>
          </table>
        </div>
        <div class="mt-4 flex justify-end">
          <button id="g_cerrar" class="btn-sec">Cerrar</button>
        </div>
      </div>`;

    const gErr = dlgGrupos.querySelector("#g_err") as HTMLElement;
    const fallo = (e: unknown): void => {
      gErr.textContent = e instanceof Error ? e.message : "Error";
    };

    dlgGrupos.querySelector("#g_cerrar")?.addEventListener("click", () => dlgGrupos.close());
    dlgGrupos.querySelector("#g_crear")?.addEventListener("click", async () => {
      gErr.textContent = "";
      const inp = dlgGrupos.querySelector("#g_nuevo") as HTMLInputElement;
      const nombre = inp.value.trim();
      if (!nombre) {
        gErr.textContent = "Escribi un nombre";
        return;
      }
      try {
        await api.post("/api/grupos", { nombre });
        await recargarGrupos();
      } catch (e) {
        fallo(e);
      }
    });

    dlgGrupos.querySelector("#g_tbody")?.addEventListener("click", async (ev) => {
      const t = ev.target as HTMLElement;
      gErr.textContent = "";
      const guardar = t.closest("[data-guardar-grupo]");
      if (guardar) {
        const id = Number(guardar.getAttribute("data-guardar-grupo"));
        const inp = dlgGrupos.querySelector(`[data-nombre="${id}"]`) as HTMLInputElement;
        try {
          await api.put(`/api/grupos/${id}`, { nombre: inp.value.trim(), activo: 1 });
          await recargarGrupos();
        } catch (e) {
          fallo(e);
        }
        return;
      }
      const borrar = t.closest("[data-borrar-grupo]");
      if (borrar) {
        const id = Number(borrar.getAttribute("data-borrar-grupo"));
        try {
          await api.del(`/api/grupos/${id}`);
          await recargarGrupos();
        } catch (e) {
          fallo(e);
        }
      }
    });
  }

  async function recargarGrupos(): Promise<void> {
    grupos = await api.get<GrupoProduccion[]>("/api/grupos");
    await cargar(); // los productos traen el nombre del grupo en la tabla
    pintarGrupos();
  }

  contenedor.querySelector("#verGrupos")?.addEventListener("click", () => {
    pintarGrupos();
    dlgGrupos.showModal();
  });

  await cargar();
  sugerir();
}
